import React, { FC, useState } from 'react';
import { Stack, SxProps, Theme, Tooltip } from '@mui/material';
import { HeroSectionFormat, TextDetails } from 'ui-component/Hero';
import WithSkeleton from 'ui-component/extended/WithSkeleton';
import CopyClipboard from 'ui-component/extended/CopyClipboard';
import SegmentIconButton from 'ui-component/Segment/SegmentIconButton';
import { IconPencil } from '@tabler/icons-react';
import { PermissionScope } from 'types/apps';
import InlineGoToButton from 'ui-component/shared/InlineGoToButton';
import HeroSectionRenderComponent from 'ui-component/Hero/HeroSectionRenderComponent';

interface HeroSectionProps extends TextDetails {
  isLoading: boolean;
  width: number;
  variant?: 'body1' | 'h3';
  format?: HeroSectionFormat;
  sx?: SxProps<Theme>;
  permissionScope?: PermissionScope;
  disabled?: boolean;
  tooltip?: string;
  editButtonTooltip?: string;
}

const HeroSection: FC<HeroSectionProps> = ({
  isLoading,
  width,
  variant = 'body1',
  format = HeroSectionFormat.TYPOGRAPHY,
  value,
  clipboard,
  clipboardValue,
  children,
  editInline,
  sx,
  permissionScope,
  disabled = false,
  tooltip,
  link,
  linkTooltip,
  editButtonTooltip,
}) => {
  const [isEditing, setIsEditing] = useState(false);

  let clonedEditInline = null;
  if (editInline) {
    clonedEditInline = React.cloneElement(editInline, {
      closeEditing: () => setIsEditing(false),
    });
  }
  return (
    <WithSkeleton isLoading={isLoading} width={width}>
      <Stack direction="row" alignItems="center">
        {editInline && isEditing ? (
          clonedEditInline
        ) : (
          <Tooltip title={tooltip}>
            <>
              <HeroSectionRenderComponent
                format={format}
                value={value || ''}
                variant={variant}
                sx={sx}
              >
                {editInline && !isEditing && (
                  <SegmentIconButton
                    tooltip={editButtonTooltip}
                    color="primary"
                    onClick={() => setIsEditing(true)}
                    size="small"
                    permissionScope={permissionScope}
                    disabled={disabled}
                  >
                    <IconPencil stroke="1.5px" width={20} height={20} />
                  </SegmentIconButton>
                )}
              </HeroSectionRenderComponent>
            </>
          </Tooltip>
        )}
        {!(editInline && isEditing) && clipboard && (
          <CopyClipboard
            input={clipboardValue || value || ''}
            label={clipboard}
          />
        )}
        {!!link && (
          <InlineGoToButton link={link} tooltip={linkTooltip || 'View'} />
        )}
        {children}
      </Stack>
    </WithSkeleton>
  );
};

export default HeroSection;
